import { createApi } from "@reduxjs/toolkit/query/react";
import axiosBaseQuery from "../../../utils/Customer/apiBaseQuery";
import {
  IAdminOrderDetailsResponse,
  IAdminOrdersRequest,
  IAdminOrdersResponse,
  IAdminRefundRequest,
  IAdminRefundResponse,
  ICreateReturnRequest,
  IOrderFullFIllRequest,
  IOrderReviewActionRequest,
  ISubmitReviewOrderRequest,
  IUpdatePetsRemainingPaymentRequest,
} from "../../../types/Request/AdminRequest";
import {
  IGetPendingReviewsResponse,
  IGetPendingRevisionPaymentsResponse,
  IOrderReviewDetailsResponse,
  IOrderRevisionDetailsResponse,
  IOrderStatusHistoryResponse,
  IOrderTimelineResponse,
  IRecommendedProductsResponse,
  ISubmitOrderReviewResponse,
  OrderActivitiesResponse,
} from "../../../types/Response/AdminResponse";
import {
  ADMIN_ORDER_WORKFLOW,
  ADMIN_ORDER_WORKFLOW_CONFIRM_PAYMENT,
  ADMIN_ORDER_WORKFLOW_REVIEW,
  ADMIN_ORDER_WORKFLOW_REVIEWS_PENDING,
  ADMIN_ORDER_WORKFLOW_REVISIONS,
  ADMIN_ORDER_WORKFLOW_REVISIONS_PENDING_PAYMENT,
  ADMIN_ORDERS,
} from "../../../config/apiUrlsAdmin";

export const adminOrderAPi = createApi({
  reducerPath: "adminOrderAPi",
  baseQuery: axiosBaseQuery(),
  tagTypes: ["Orders"], // Add this tag type

  endpoints: (builder) => ({
    getAdminOrders: builder.query<IAdminOrdersResponse, IAdminOrdersRequest>({
      query: (data) => ({
        url: ADMIN_ORDERS,
        method: "get",
        data: data,
      }),
      providesTags: ["Orders"],
    }),

    getAdminOrderById: builder.query<IAdminOrderDetailsResponse, number>({
      query: (orderId) => ({
        url: `${ADMIN_ORDERS}/${orderId}`,
        method: "get",
      }),
      providesTags: ["Orders"],
    }),

    OrderWorkFlowReview: builder.mutation<
      ISubmitOrderReviewResponse,
      IOrderReviewActionRequest
    >({
      query: (data) => ({
        url: ADMIN_ORDER_WORKFLOW_REVIEW,
        method: "post",
        data: data,
      }),
      invalidatesTags: ["Orders"],
    }),

    SubmitOrderReview: builder.mutation<
      ISubmitOrderReviewResponse,
      { orderId: number; data: ISubmitReviewOrderRequest }
    >({
      query: ({ orderId, data }) => ({
        url: `${ADMIN_ORDER_WORKFLOW}/${orderId}/submit-review`,
        method: "post",
        data: data,
      }),
      invalidatesTags: ["Orders"],
    }),

    ReviewsPending: builder.query<IGetPendingReviewsResponse, void>({
      query: () => ({
        url: ADMIN_ORDER_WORKFLOW_REVIEWS_PENDING,
        method: "get",
      }),
      providesTags: ["Orders"],
    }),

    ReviewsPendingByOrderId: builder.query<IOrderReviewDetailsResponse, number>(
      {
        query: (orderId) => ({
          url: `${ADMIN_ORDER_WORKFLOW}/${orderId}/review`,
          method: "get",
        }),
        providesTags: ["Orders"],
      },
    ),
    replacementProducts: builder.query<
      IRecommendedProductsResponse,
      { orderId: number; orderItemId: number; search: string }
    >({
      query: ({ orderId, orderItemId, search }) => ({
        url: `${ADMIN_ORDER_WORKFLOW}/${orderId}/items/${orderItemId}/replacements?search=${search}`,
        method: "get",
      }),
      providesTags: ["Orders"],
    }),

    RevisionByRevisionId: builder.query<IOrderRevisionDetailsResponse, number>({
      query: (revisionId) => ({
        url: `${ADMIN_ORDER_WORKFLOW_REVISIONS}/${revisionId}`,
        method: "get",
      }),
      providesTags: ["Orders"],
    }),

    RevisionPendingPayments: builder.query<
      IGetPendingRevisionPaymentsResponse,
      void
    >({
      query: () => ({
        url: ADMIN_ORDER_WORKFLOW_REVISIONS_PENDING_PAYMENT,
        method: "get",
      }),
      providesTags: ["Orders"],
    }),

    OrderTimeLine: builder.query<IOrderTimelineResponse, number>({
      query: (orderId) => ({
        url: `${ADMIN_ORDER_WORKFLOW}/${orderId}/timeline`,
        method: "get",
      }),
      providesTags: ["Orders"],
    }),

    orderStatusHistory: builder.query<IOrderStatusHistoryResponse, number>({
      query: (orderId) => ({
        url: `${ADMIN_ORDER_WORKFLOW}/${orderId}/status-history`,
        method: "get",
      }),
      providesTags: ["Orders"],
    }),

    orderFullFillUpdate: builder.mutation<
      void,
      { orderId: number; data: IOrderFullFIllRequest }
    >({
      query: ({ orderId, data }) => ({
        url: `${ADMIN_ORDER_WORKFLOW}/${orderId}/fulfillment/manual`,
        method: "put",
        data: data,
      }),
      invalidatesTags: ["Orders"],
    }),

        markOrderFullyPaid: builder.mutation<
      void,
    IUpdatePetsRemainingPaymentRequest 
    >({
      query: (data ) => ({
        url: ADMIN_ORDER_WORKFLOW_CONFIRM_PAYMENT,
        method: "post",
        data: data,
      }),
      invalidatesTags: ["Orders"],
    }),
    orderActivity: builder.query<OrderActivitiesResponse, number>({
      query: (orderId) => ({
        url: `${ADMIN_ORDER_WORKFLOW}/${orderId}/activity`,
        method: "get",
      }),
      providesTags: ["Orders"],
    }),

    refundToCustomer: builder.mutation<
      IAdminRefundResponse,
      { orderId: number; data: IAdminRefundRequest }
    >({
      query: ({ orderId, data }) => ({
        url: `${ADMIN_ORDERS}/${orderId}/refund`,
        method: "post",
        data: data,
      }),
      invalidatesTags: ["Orders"],
    }),

    returnOrder: builder.mutation<
      void,
      { orderId: number; data: ICreateReturnRequest }
    >({
      query: ({ orderId, data }) => ({
        url: `${ADMIN_ORDER_WORKFLOW}/${orderId}/returns`,
        method: "post",
        data: data,
      }),
      invalidatesTags: ["Orders"],
    }),
  }),
});

export const {
  useLazyGetAdminOrdersQuery,
  useLazyGetAdminOrderByIdQuery,
  useOrderWorkFlowReviewMutation,
  useLazyReviewsPendingQuery,
  useLazyReviewsPendingByOrderIdQuery,
  useLazyReplacementProductsQuery,
  useLazyOrderStatusHistoryQuery,
  useLazyOrderTimeLineQuery,
  useLazyRevisionByRevisionIdQuery,
  useLazyRevisionPendingPaymentsQuery,

  useSubmitOrderReviewMutation,
  useOrderFullFillUpdateMutation,
  useRefundToCustomerMutation,
  useLazyOrderActivityQuery,
  useReturnOrderMutation,
  useMarkOrderFullyPaidMutation
} = adminOrderAPi;
